// src/app/components/password-change-card.tsx
import { useState } from "react";
import { Eye, EyeOff, Lock, KeyRound, Loader2, Check, AlertCircle } from "lucide-react";
import { changePassword } from "@/app/services/common/change-password-service";
import { notifyAuthExpired } from "@/api/api-fetch";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import {
  PasswordRequirements,
  checkPassword,
} from "../components/login/password-requirements";

/** How long the "Password updated" confirmation stays up before we drop the
 *  user on the login screen. Long enough to read, short enough to feel snappy. */
const LOGOUT_DELAY_MS = 2500;

interface PasswordChangeCardProps {
  /** Pass the same paddings the rest of the settings cards use */
  cardHeaderPad?: string;
  cardBodyPad?: number;
  /** Called after a successful change so parent can show a toast etc. */
  onSuccess?: () => void;
}

export function PasswordChangeCard({
  cardHeaderPad = "20px 24px",
  cardBodyPad = 32,
  onSuccess,
}: PasswordChangeCardProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword,     setNewPassword]     = useState("");
  const [showCurrent,     setShowCurrent]     = useState(false);
  const [showNew,         setShowNew]         = useState(false);

  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Once the change succeeds we're on a one-way trip to the login screen;
  // keep the form locked so nothing can be re-submitted mid-logout.
  const [loggingOut, setLoggingOut] = useState(false);

  const checks = checkPassword(newPassword);
  const sameAsCurrent =
    currentPassword.length > 0 &&
    newPassword.length > 0 &&
    currentPassword === newPassword;

  const canSubmit =
    !saving &&
    currentPassword.length > 0 &&
    checks.allValid &&
    !sameAsCurrent;

  // Step 1 — validate, then open the themed "you'll be signed out" alert.
  // The password isn't touched until the user confirms.
  const handleRequestSave = () => {
    setError(null);
    setSuccess(null);

    if (!currentPassword) {
      setError("Please enter your current password.");
      return;
    }
    if (!checks.allValid) {
      setError("New password doesn't meet the requirements below.");
      return;
    }
    if (sameAsCurrent) {
      setError("New password must be different from your current password.");
      return;
    }

    setConfirmOpen(true);
  };

  // Step 2 — user acknowledged the alert. Change the password, confirm, then
  // drop them on the login screen. The backend revokes every session on a
  // successful change, so this session's token is already dead; we just give
  // the user a beat to read "Password updated" before routing to /login.
  const handleConfirmSave = async () => {
    setConfirmOpen(false);
    setError(null);
    setSaving(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setSuccess("Password updated. Signing you out…");
      setLoggingOut(true);
      onSuccess?.();
      window.setTimeout(() => {
        // App.tsx listens for this and runs the standard logout → login route.
        notifyAuthExpired("/auth/change-password");
      }, LOGOUT_DELAY_MS);
    } catch (e: any) {
      setError(e?.message || "Failed to update password.");
      setSaving(false);
    }
  };

  return (
    <>
    <div style={{ marginBottom: "24px" }}>
      <div
        style={{
          backgroundColor: "var(--background)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "var(--elevation-sm)",
        }}
      >
        <div
          style={{
            padding: cardHeaderPad,
            borderBottom: "1px solid var(--border)",
            backgroundColor: "var(--muted-30)",
          }}
        >
          <h3 style={{ fontSize: "var(--text-xl)" }}>Password</h3>
          <p
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--muted-foreground)",
              marginTop: 4,
            }}
          >
            Re-enter your current password to set a new one.
          </p>
        </div>

        <div style={{ padding: `${cardBodyPad}px` }}>
          {/* Current password */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
              Current password
            </label>
            <div style={{ position: "relative" }}>
              <Lock
                style={{
                  position: "absolute", left: 14, top: "50%",
                  transform: "translateY(-50%)",
                  width: 16, height: 16,
                  color: "var(--muted-foreground)",
                  pointerEvents: "none",
                }}
              />
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setError(null);
                  setSuccess(null);
                }}
                placeholder="Enter your current password"
                disabled={saving}
                autoComplete="current-password"
                style={{
                  width: "100%",
                  padding: "12px 44px 12px 40px",
                  backgroundColor: "var(--input-background)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  fontSize: "var(--text-base)",
                  color: "var(--foreground)",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                onClick={() => setShowCurrent((s) => !s)}
                disabled={saving}
                aria-label={showCurrent ? "Hide password" : "Show password"}
                style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer",
                  padding: 4, color: "var(--muted-foreground)",
                  display: "flex", alignItems: "center",
                }}
              >
                {showCurrent
                  ? <EyeOff style={{ width: 16, height: 16 }} />
                  : <Eye style={{ width: 16, height: 16 }} />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
              New password
            </label>
            <div style={{ position: "relative" }}>
              <Lock
                style={{
                  position: "absolute", left: 14, top: "50%",
                  transform: "translateY(-50%)",
                  width: 16, height: 16,
                  color: "var(--muted-foreground)",
                  pointerEvents: "none",
                }}
              />
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError(null);
                  setSuccess(null);
                }}
                placeholder="Enter a new password"
                disabled={saving}
                autoComplete="new-password"
                style={{
                  width: "100%",
                  padding: "12px 44px 12px 40px",
                  backgroundColor: "var(--input-background)",
                  border: `1px solid ${
                    newPassword && !checks.allValid
                      ? "var(--infrared-coral)"
                      : "var(--border)"
                  }`,
                  borderRadius: "var(--radius)",
                  fontSize: "var(--text-base)",
                  color: "var(--foreground)",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="button"
                onClick={() => setShowNew((s) => !s)}
                disabled={saving}
                aria-label={showNew ? "Hide password" : "Show password"}
                style={{
                  position: "absolute", right: 12, top: "50%",
                  transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer",
                  padding: 4, color: "var(--muted-foreground)",
                  display: "flex", alignItems: "center",
                }}
              >
                {showNew
                  ? <EyeOff style={{ width: 16, height: 16 }} />
                  : <Eye style={{ width: 16, height: 16 }} />}
              </button>
            </div>
          </div>

          {/* Requirements checklist (only shown once user starts typing) */}
          {newPassword && (
            <div style={{ marginBottom: 18 }}>
              <PasswordRequirements password={newPassword} />
            </div>
          )}

          {/* Same-as-current warning */}
          {sameAsCurrent && (
            <div
              style={{
                marginBottom: 14,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid var(--infrared-coral)",
                backgroundColor: "var(--infrared-coral-5)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <AlertCircle
                style={{
                  width: 16, height: 16, flexShrink: 0, marginTop: 2,
                  color: "var(--infrared-coral)",
                }}
              />
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--infrared-coral)",
                }}
              >
                New password must be different from your current password.
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 14,
                padding: "12px 14px",
                borderRadius: 8,
                border: "1px solid var(--infrared-coral)",
                backgroundColor: "var(--infrared-coral-5)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <AlertCircle
                style={{
                  width: 18, height: 18, flexShrink: 0, marginTop: 1,
                  color: "var(--infrared-coral)",
                }}
              />
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--infrared-coral)",
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            </div>
          )}

          {/* Success */}
          {success && (
            <div
              role="status"
              style={{
                marginBottom: 14,
                padding: "12px 14px",
                borderRadius: 8,
                border: "1px solid var(--signal-green)",
                backgroundColor: "var(--alpha-teal-5)",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <Check
                style={{
                  width: 18, height: 18, flexShrink: 0, marginTop: 1,
                  color: "var(--signal-green)",
                }}
              />
              <div
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                }}
              >
                {success}
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="button"
            onClick={handleRequestSave}
            disabled={!canSubmit || loggingOut}
            className="w-full sm:w-auto"
            style={{
              height: 44,
              padding: "0 18px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              backgroundColor: canSubmit ? "var(--accent)" : "var(--muted)",
              color: canSubmit ? "var(--accent-foreground)" : "var(--muted-foreground)",
              border: "none",
              borderRadius: "var(--radius)",
              cursor: canSubmit ? "pointer" : "not-allowed",
              fontWeight: "var(--font-weight-semibold)",
              opacity: saving ? 0.85 : 1,
            }}
          >
            {saving
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <KeyRound className="w-4 h-4" />}
            {loggingOut ? "Signing you out…" : "Update password"}
          </button>

          <p
            style={{
              marginTop: 12,
              fontSize: "var(--text-xs)",
              color: "var(--muted-foreground)",
            }}
          >
            For your security, all other devices will be signed out after a
            successful password change.
          </p>
        </div>
      </div>
    </div>

    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      onConfirm={handleConfirmSave}
      title="You'll be signed out"
      description={
        "Updating your password signs you out of this device and everywhere " +
        "else you're logged in. You'll need to sign back in with your new " +
        "password."
      }
      confirmLabel="Update & sign out"
      cancelLabel="Cancel"
    />
    </>
  );
}